import {
  ArrowLeftRight,
  Boxes,
  CalendarDays,
  ChartNoAxesColumn,
  Gamepad2,
  UserSearch,
  GitCompareArrows,
  RefreshCcwDot,
  Scale,
  ScrollText,
  Telescope,
  type LucideIcon,
} from 'lucide-react';

/**
 * The app's functional areas. betlens started as one screen; this registry is
 * what makes it a multi-tool. Adding a feature = one entry here plus its
 * component — the navbar, routing and deep-linking all follow from the registry.
 */
export type ViewId =
  | 'inspection'
  | 'transactions'
  | 'delivery'
  | 'reports'
  | 'games'
  | 'player'
  | 'finance'
  | 'reconciliation'
  | 'rates'
  | 'compare'
  | 'rules';

export interface NavView {
  id: ViewId;
  label: string;
  icon: LucideIcon;
  blurb: string;
  /** Placeholders for the roadmap — visible in the nav, not yet built. */
  ready: boolean;
}

export const VIEWS: NavView[] = [
  {
    id: 'inspection',
    label: 'Bet Inspection',
    icon: Telescope,
    blurb: "The whole story of one bet across every datasource, with rule-driven hints.",
    ready: true,
  },
  {
    id: 'transactions',
    label: 'Transactions',
    icon: Boxes,
    blurb: 'Look up a transaction by any id and see it across all four stores.',
    ready: true,
  },
  {
    id: 'delivery',
    label: 'Delivery Retries',
    icon: RefreshCcwDot,
    blurb: 'Wins and rollbacks that failed to reach the operator and are being retried.',
    ready: true,
  },
  {
    id: 'reports',
    label: 'Player Reports',
    icon: ChartNoAxesColumn,
    blurb: 'Per-player bets, wins and GGR for a period, by project and currency.',
    ready: true,
  },
  {
    id: 'games',
    label: 'Game Reports',
    icon: Gamepad2,
    blurb: 'Per-bet history for one game — stake, payout and rollbacks, bet by bet.',
    ready: true,
  },
  {
    id: 'player',
    label: 'Player Inspection',
    icon: UserSearch,
    blurb: 'One player across every unit — games, currency, activity, devices — with fraud signals.',
    ready: true,
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: CalendarDays,
    blurb: 'Day-by-day stake, win and gross gaming revenue for a project.',
    ready: true,
  },
  {
    id: 'reconciliation',
    label: 'Reconciliation',
    icon: Scale,
    blurb: 'The reconciliation feed a partner receives for a period — view and export it.',
    ready: true,
  },
  {
    id: 'rates',
    label: 'Exchange Rates',
    icon: ArrowLeftRight,
    blurb: 'The FX table this portal converts with — and how stale each rate is.',
    ready: true,
  },
  {
    id: 'compare',
    label: 'Compare',
    icon: GitCompareArrows,
    blurb: 'Put two bets side by side and diff them — attributes, hints, and sources.',
    ready: true,
  },
  {
    id: 'rules',
    label: 'Rule Builder',
    icon: ScrollText,
    blurb: 'Author and preview hint rules against real bets, and store them.',
    ready: true,
  },
];

export const DEFAULT_VIEW: ViewId = 'inspection';

export function isViewId(value: string | null): value is ViewId {
  return VIEWS.some((v) => v.id === value);
}

export function viewById(id: ViewId): NavView {
  return VIEWS.find((v) => v.id === id) ?? VIEWS[0];
}
